import { EventEmitter, Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { StorageService } from './storage.service';
import { OrdersService } from './orders.service';
import {
  AlertController,
  LoadingController,
  ModalController,
} from '@ionic/angular';
import * as L from 'leaflet';
import { GeojsonService } from './geojson.service';
import { Router } from '@angular/router';
import { Directory, Encoding, Filesystem } from '@capacitor/filesystem';
import { LocationAccuracy } from '@awesome-cordova-plugins/location-accuracy/ngx';
import { Feature, Point, FeatureCollection, LineString } from 'geojson';
import { MessagePopupComponent } from '../modals/message-popup/message-popup.component';
import { TranslateService } from '@ngx-translate/core';
import { LocationService } from './location.service';

@Injectable({
  providedIn: 'root',
})
export class MapService {
  public readonly KEYS_LIST_KEY = 'tile_keys_list';
  public readonly TARGET_ZOOM_LEVEL = 15;
  public networkStatus = 'offline';
  userLatitude;
  userLongitude;
  claimedOrders = [];
  thirdPartyOrders = [];
  thirdPartyPagination = {
    page: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
    loading: false,
    allLoaded: false,
    search: '',
  };

  invokeEvent: Subject<any> = new Subject();

  constructor(
    private router: Router,
    private storageService: StorageService,
    private ordersService: OrdersService,
    private geojsonService: GeojsonService,
    private locationAccuracy: LocationAccuracy,
    private loadingController: LoadingController,
    private modalController: ModalController,
    private translateService: TranslateService,
    private alertController: AlertController,
    private locationService: LocationService
  ) {}

  setOnline() {
    this.networkStatus = 'online';
    // Reset pagination state when coming back online
    this.thirdPartyPagination.allLoaded = false;
    this.invokeEvent.next('set-online');
  }

  setOffline() {
    this.networkStatus = 'offline';
    this.invokeEvent.next('set-offline');
  }

  nextCurrentOrder() {
    this.invokeEvent.next('next-current-order');
  }

  async getOrders(syncPopup) {
    await this.syncData(syncPopup);
    await this.getClaimedOrders();
    await this.getThirdPartyOrders();
  }

  async syncData(syncPopup) {
    console.log('this.networkStatus:');
    console.log(this.networkStatus);
    if (this.networkStatus == 'offline') {
      return;
    }
    const hasToSync = await this.hasSomethingToSync();
    console.log('hasToSync:');
    console.log(hasToSync);
    let loading;
    if (syncPopup && hasToSync) {
      const synchronizationInProgress = this.translateService.instant(
        'synchronization-in-progress'
      );
      loading = await this.loadingController.create({
        message: synchronizationInProgress,
      });
      loading.present();
    }
    await this.retryFailedRequests();
    if (syncPopup && hasToSync) {
      loading.dismiss();
    }

    if (syncPopup && hasToSync) {
      let modal = await this.modalController.create({
        component: MessagePopupComponent,
        componentProps: {
          title: 'synchronization',
          text: 'synchronization-text',
          buttonText: 'okay',
        },
        cssClass: 'alert-modal',
      });
      modal.present();
    }
  }

  async hasSomethingToSync() {
    let result = false;
    const retryQueue =
      JSON.parse(await this.storageService.get('retryQueue')) || [];
    if (retryQueue.length > 0) {
      result = true;
    }
    return result;
  }

  async getThirdPartyOrders(loadMore = false) {
    if (!loadMore) {
      // Reset pagination if not loading more
      this.thirdPartyPagination.page = 1;
      this.thirdPartyPagination.allLoaded = false;
      this.thirdPartyOrders =
        JSON.parse(await this.storageService.get('thirdPartyOrders')) || [];
    }

    if (
      this.thirdPartyPagination.loading ||
      this.thirdPartyPagination.allLoaded
    ) {
      return;
    }

    // Check if we're offline and this is a loadMore request
    if (this.networkStatus === 'offline' && loadMore) {
      // Don't make API calls when offline for pagination
      this.thirdPartyPagination.allLoaded = true;
      return;
    }

    this.thirdPartyPagination.loading = true;

    this.ordersService
      .fetchOrdersThirdParty({
        page: this.thirdPartyPagination.page,
        pageSize: this.thirdPartyPagination.pageSize,
        search: this.thirdPartyPagination.search,
      })
      .subscribe(
        async (response: any) => {
          console.log('fetchOrdersThirdParty response:');
          console.log(response);

          if (loadMore) {
            // Append new items to existing array
            this.thirdPartyOrders = [
              ...this.thirdPartyOrders,
              ...response.items,
            ];
          } else {
            // Replace array with first page
            this.thirdPartyOrders = response.items;
          }

          // Update pagination metadata
          this.thirdPartyPagination.totalItems = response.totalItems;
          this.thirdPartyPagination.totalPages = response.totalPages;
          this.thirdPartyPagination.allLoaded =
            this.thirdPartyPagination.page >= response.totalPages;
          this.thirdPartyPagination.loading = false;

          // Increment page for next load
          this.thirdPartyPagination.page++;

          await this.storageService.set(
            'thirdPartyOrders',
            JSON.stringify(this.thirdPartyOrders)
          );
        },
        (error) => {
          this.thirdPartyPagination.loading = false;
          console.error('Error fetching third party orders:', error);

          // If this was a loadMore request and it failed, mark as allLoaded
          // to prevent infinite retry loops when offline
          if (loadMore) {
            this.thirdPartyPagination.allLoaded = true;
          }
        }
      );
  }

  async getClaimedOrders() {
    this.claimedOrders =
      JSON.parse(await this.storageService.get('claimedOrders')) || [];
    await this.setCurrentOrder();
    this.nextCurrentOrder();

    this.ordersService.fetchOrders().subscribe(async (response: any) => {
      console.log('fetchOrders response:');
      console.log(response);
      const actualResponse = JSON.stringify(response);
      const actualClaimedOrders = JSON.stringify(this.claimedOrders);
      this.claimedOrders = response;
      await this.storageService.set(
        'claimedOrders',
        JSON.stringify(this.claimedOrders)
      );

      const difference = this.arraysDiffer(
        JSON.parse(actualResponse),
        JSON.parse(actualClaimedOrders)
      );
      console.log('difference:');
      console.log(difference);
      if (difference) {
        await this.setCurrentOrder();
        this.nextCurrentOrder();
        await this.syncMap();
      }
    });
  }

  async markAsDelivered(order_id, saveOffline): Promise<void> {
    return new Promise((resolve) => {
      this.ordersService.markAsDelivered(order_id).subscribe(
        (response) => {
          console.log('markAsDelivered response:');
          console.log(response);
          if (saveOffline) {
            this.getOrders(false);
          }
          resolve();
        },
        async (error) => {
          if (this.networkStatus == 'offline' && saveOffline) {
            let claimedOrders =
              JSON.parse(await this.storageService.get('claimedOrders')) || [];

            claimedOrders = claimedOrders.filter(
              (item) => item.id !== order_id
            );

            await this.storageService.set(
              'claimedOrders',
              JSON.stringify(claimedOrders)
            );

            this.addToRetryQueue({
              type: 'markAsDelivered',
              params: { order_id: order_id },
            });
            this.getOrders(false);
          }
          resolve();
        }
      );
    });
  }

  async addToRetryQueue(request: any) {
    const retryQueue =
      JSON.parse(await this.storageService.get('retryQueue')) || [];
    retryQueue.push(request);
    await this.storageService.set('retryQueue', JSON.stringify(retryQueue));
  }

  async retryFailedRequests() {
    const retryQueue =
      JSON.parse(await this.storageService.get('retryQueue')) || [];
    while (retryQueue.length > 0) {
      const request = retryQueue.shift();
      console.log('Handling request:', request);
      try {
        await this.handleRequest(request);
      } catch (error) {
        console.error('Error handling request:', error);
        // Optionally, add the request back to the retry queue or handle it differently
      }
    }
    await this.storageService.remove('retryQueue');
  }

  async handleRequest(request: any) {
    console.log('request:');
    console.log(request);
    switch (request.type) {
      case 'markAsDelivered':
        return await this.markAsDelivered(request.params.order_id, false);
      default:
        throw new Error('Unknown request type');
    }
  }

  async syncMap() {
    if (this.claimedOrders.length > 0 && this.networkStatus == 'online') {
      await this.removeOutdatedTiles();

      // Ensure we have user location before proceeding
      if (!this.userLatitude || !this.userLongitude) {
        try {
          // Try to get current position
          const position = await this.locationService.getCurrentPosition({
            timeout: 10000,
            enableHighAccuracy: true,
          });

          this.userLatitude = position.coords.latitude;
          this.userLongitude = position.coords.longitude;

          console.log(
            'User location obtained:',
            this.userLatitude,
            this.userLongitude
          );
        } catch (error) {
          console.error('Could not get user location in syncMap:', error);
          // Continue with just the claimed orders coordinates
        }
      }

      let targetCoords: L.LatLng[] = [];

      if (this.userLatitude && this.userLongitude) {
        const coords = new L.LatLng(this.userLatitude, this.userLongitude);
        targetCoords.push(coords);
      }

      for (let i = 0; i < this.claimedOrders.length; i++) {
        const coords = new L.LatLng(
          this.claimedOrders[i].latitude,
          this.claimedOrders[i].longitude
        );
        targetCoords.push(coords);
      }

      if (targetCoords.length > 0) {
        await this.saveGeoJson(targetCoords);

        await this.saveTilesFromNEtoSW(targetCoords, this.TARGET_ZOOM_LEVEL);
      }
    }
  }

  saveGeoJson(targetCoords): Promise<void> {
    return new Promise<void>(async (resolve, reject) => {
      const { south, west, north, east } = this.getNSEW(targetCoords);
      this.geojsonService.fetchGeoJson(south, west, north, east).subscribe(
        async (response) => {
          const geojson = this.geojsonService.convertToGeoJson(response);
          await this.geojsonService.saveGeoJson(geojson);
          resolve();
        },
        (error) => {
          console.log('error:');
          console.log(error);
          reject();
        }
      );
    });
  }

  private async saveTilesFromNEtoSW(targetCoords, targetZoom): Promise<void> {
    const { northEast, southWest } = this.getBoundingBox(targetCoords);
    const tileRange = this.getTileRangeFromBounds(
      northEast,
      southWest,
      targetZoom
    );

    return new Promise<void>(async (resolve, reject) => {
      try {
        for (let x = tileRange.min.x; x <= tileRange.max.x; x++) {
          for (let y = tileRange.min.y; y <= tileRange.max.y; y++) {
            const tileUrl = this.getTileLink(x, y, targetZoom);
            try {
              const response = await fetch(tileUrl);
              if (response.ok) {
                const blob = await response.blob();
                const base64Tile = await this.blobToBase64(blob);
                const coords = {
                  z: targetZoom,
                  x,
                  y,
                };
                const tileKey = `tile_${coords.z}_${coords.x}_${coords.y}`;
                await this.saveTileAsFile(tileKey, base64Tile);
                await this.addTileKey(tileKey);
              }
            } catch (error) {
              console.error(
                `Failed to fetch tile at x: ${x}, y: ${y}, zoom: ${targetZoom}`,
                error
              );
            }
          }
        }

        resolve();
      } catch (error) {
        console.error('Error in saving tiles:', error);
        reject(error);
      }
    });
  }

  private async saveTileAsFile(
    tileKey: string,
    base64Tile: string
  ): Promise<void> {
    try {
      await Filesystem.writeFile({
        path: `tiles/${tileKey}.png`,
        data: base64Tile,
        directory: Directory.Data,
        encoding: Encoding.UTF8, // Base64 strings are text, so use UTF8
        recursive: true,
      });
      console.log(`Tile ${tileKey} saved successfully`);
    } catch (error) {
      console.error('Error saving tile:', error);
    }
  }

  async getTileFile(tileKey: string): Promise<string | null> {
    try {
      const file = await Filesystem.readFile({
        path: `tiles/${tileKey}.png`,
        directory: Directory.Data,
        encoding: Encoding.UTF8,
      });
      let fileData = file.data + '';
      return fileData;
    } catch (error) {
      return null;
    }
  }

  private async removeOutdatedTiles() {
    const tileKeys = await this.getTileKeys();
    for (const tileKey of tileKeys) {
      try {
        // Remove the tile from the filesystem
        await Filesystem.deleteFile({
          path: `tiles/${tileKey}.png`,
          directory: Directory.Data, // Or another directory based on your use
        });

        this.removeTileKey(tileKey);
        console.log(`Tile ${tileKey} removed successfully.`);
      } catch (error) {
        console.error(`Failed to remove tile ${tileKey}:`, error);
      }
    }
  }

  private async removeTileKey(tileKey: string) {
    let keysList = await this.getTileKeys();
    keysList = keysList.filter((key) => key !== tileKey);
    await this.storageService.set(this.KEYS_LIST_KEY, JSON.stringify(keysList));
  }

  private getBoundingBox(targetCoords): {
    northEast: L.LatLng;
    southWest: L.LatLng;
  } {
    const latitudes = targetCoords.map((coord) => coord.lat);
    const longitudes = targetCoords.map((coord) => coord.lng);

    const northEast = new L.LatLng(
      Math.max(...latitudes),
      Math.max(...longitudes)
    );
    const southWest = new L.LatLng(
      Math.min(...latitudes),
      Math.min(...longitudes)
    );

    return { northEast, southWest };
  }

  getNSEW(targetCoords) {
    let south = Number.POSITIVE_INFINITY;
    let west = Number.POSITIVE_INFINITY;
    let north = Number.NEGATIVE_INFINITY;
    let east = Number.NEGATIVE_INFINITY;

    targetCoords.forEach((coord) => {
      const lat = coord.lat;
      const lng = coord.lng;

      if (lat < south) south = lat;
      if (lat > north) north = lat;
      if (lng < west) west = lng;
      if (lng > east) east = lng;
    });

    const kmBuffer = 10;
    const latBuffer = kmBuffer / 111;
    const lngBuffer =
      kmBuffer / (111 * Math.cos((((south + north) / 2) * Math.PI) / 180));

    south -= latBuffer;
    north += latBuffer;
    west -= lngBuffer;
    east += lngBuffer;

    return { south, west, north, east };
  }

  private getTileRangeFromBounds(
    northEast: L.LatLng,
    southWest: L.LatLng,
    zoom: number
  ) {
    const neTile = this.latLngToTile(northEast, zoom);
    const swTile = this.latLngToTile(southWest, zoom);
    return {
      min: {
        x: Math.min(neTile.x, swTile.x),
        y: Math.min(neTile.y, swTile.y),
      },
      max: {
        x: Math.max(neTile.x, swTile.x),
        y: Math.max(neTile.y, swTile.y),
      },
    };
  }

  private latLngToTile(latLng: L.LatLng, zoom: number) {
    const lat = latLng.lat;
    const lng = latLng.lng;

    const x = Math.floor(((lng + 180) / 360) * Math.pow(2, zoom));
    const y = Math.floor(
      ((1 -
        Math.log(
          Math.tan((lat * Math.PI) / 180) + 1 / Math.cos((lat * Math.PI) / 180)
        ) /
          Math.PI) /
        2) *
        Math.pow(2, zoom)
    );

    return { x, y };
  }

  async setCurrentOrder() {
    if (this.claimedOrders.length > 0) {
      let currentOrder = await this.findNearestLocation(this.claimedOrders);
      console.log('currentOrder:');
      console.log(currentOrder);
      await this.storageService.set(
        'currentOrder',
        JSON.stringify(currentOrder)
      );
    } else {
      await this.storageService.set('currentOrder', null);
    }
  }

  private calculateDistance(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): number {
    const toRad = (value: number) => (value * Math.PI) / 180;
    const R = 6371; // Radius of the Earth in kilometers
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distance in kilometers
  }

  // Function to find the nearest location
  public async findNearestLocation(
    locations: { latitude: number; longitude: number }[]
  ): Promise<{ latitude: number; longitude: number }> {
    return new Promise((resolve, reject) => {
      let nearestLocation = null;
      let minDistance = Number.MAX_VALUE;

      console.log('findNearestLocation this.userLatitude:');
      console.log(this.userLatitude);
      console.log('findNearestLocation this.userLongitude:');
      console.log(this.userLongitude);

      locations.forEach((location) => {
        const distance = this.calculateDistance(
          this.userLatitude,
          this.userLongitude,
          location.latitude,
          location.longitude
        );
        if (distance < minDistance) {
          minDistance = distance;
          nearestLocation = location;
        }
      });

      resolve(nearestLocation);
    });
  }

  private getTileLink(x, y, z): string {
    return `https://a.tile.openstreetmap.org/${z}/${x}/${y}.png`;
  }

  private async getTileKeys(): Promise<string[]> {
    const keysList = await this.storageService.get(this.KEYS_LIST_KEY);
    return keysList ? JSON.parse(keysList) : [];
  }

  private async addTileKey(tileKey: string) {
    const keysList = await this.getTileKeys();
    if (!keysList.includes(tileKey)) {
      keysList.push(tileKey);
      await this.storageService.set(
        this.KEYS_LIST_KEY,
        JSON.stringify(keysList)
      );
    }
  }

  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64data = reader.result as string;
        resolve(base64data.split(',')[1]);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  async checkForPermissions(): Promise<void> {
    return new Promise(async (resolve, reject) => {
      try {
        await this.locationService
          .requestPermissions()
          .then((value) => {
            console.log('requestPermissions value:');
            console.log(value);
            this.invokeEvent.next('geolocation');
            resolve();
          })
          .catch(async (error) => {
            console.log('checkForPermissions error:');
            console.log(error);
            if (error.message.includes('Location services are not enabled')) {
              await this.promptEnableLocation();
            } else {
              console.error('Error getting location:', error);
            }
            reject();
          });
        await this.locationService
          .checkPermissions()
          .then((value) => {
            console.log('checkPermissions value:');
            console.log(value);
            this.invokeEvent.next('geolocation');
            resolve();
          })
          .catch(async (error) => {
            console.log('checkForPermissions error:');
            console.log(error);
            if (error.message.includes('Location services are not enabled')) {
              await this.promptEnableLocation();
            } else {
              console.error('Error getting location:', error);
            }
            reject();
          });
      } catch (error) {
        console.log('checkForPermissions error:');
        console.log(error);
        if (error.message.includes('Location services are not enabled')) {
          await this.promptEnableLocation();
        } else {
          console.error('Error getting location:', error);
        }
        reject();
      }
    });
  }

  async promptEnableLocation(): Promise<void> {
    return new Promise(async (resolve, reject) => {
      try {
        const canRequest = await this.locationAccuracy.canRequest();
        const isRequesting = await this.locationAccuracy.isRequesting();
        console.log('canRequest:');
        console.log(JSON.stringify(canRequest));
        console.log('isRequesting:');
        console.log(JSON.stringify(isRequesting));
        if (!isRequesting) {
          if (canRequest) {
            this.locationAccuracy
              .request(this.locationAccuracy.REQUEST_PRIORITY_HIGH_ACCURACY)
              .then(() => {
                console.log('Location services enabled');
                resolve();
              })
              .catch((error) => {
                console.error('Error enabling location services:', error);
                this.showEnableLocationAlert();
                reject();
              });
          } else {
            reject();
          }
        } else {
          reject();
        }
      } catch (error) {
        console.error('Error checking location accuracy:', error);
        this.showEnableLocationAlert();
        reject();
      }
    });
  }

  async showEnableLocationAlert() {
    const alert = await this.alertController.create({
      header: 'Enable Location Services',
      message:
        'Location services are required to use this feature. Please enable them in settings.',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Open Settings',
          handler: () => {
            this.openSettings();
          },
        },
      ],
    });
    await alert.present();
  }

  openSettings() {
    this.locationAccuracy
      .request(1)
      .then((value) => {
        console.log('locationAccuracy value:');
        console.log(JSON.stringify(value));
      })
      .catch((error) => {
        console.log('locationAccuracy error:');
        console.log(JSON.stringify(error));
      });
  }

  arraysDiffer(arr1, arr2) {
    // Helper function to serialize objects to strings
    const serialize = (obj) => JSON.stringify(obj);

    // Convert arrays to sets of serialized objects
    const set1 = new Set(arr1.map(serialize));
    const set2 = new Set(arr2.map(serialize));

    // Check if the sets have the same size and same elements
    if (set1.size !== set2.size) return true;

    // Check if every element in set1 is in set2 and vice versa
    for (const item of set1) {
      if (!set2.has(item)) return true;
    }

    return false;
  }
}
